import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { Save, Send } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import adminService from '../../services/api/adminService';

const SettingsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { control, handleSubmit } = useForm();

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const data = await adminService.getConfigurations();
      setConfigurations(data);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  const onGeneralSubmit = async (data) => {
    try {
      setLoading(true);
      // Save general settings
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const onEmailSubmit = async (data) => {
    try {
      setLoading(true);
      // Save email settings
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const onWhatsAppSubmit = async (data) => {
    try {
      setLoading(true);
      // Save WhatsApp settings
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        System Settings
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Paper sx={{ mt: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="General" />
          <Tab label="Email" />
          <Tab label="WhatsApp" />
          <Tab label="Booking" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <form onSubmit={handleSubmit(onGeneralSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="siteName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Site Name"
                        defaultValue="Umrah Booking Platform"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="contactEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Contact Email"
                        type="email"
                        defaultValue="info@umrahbooking.com"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="contactPhone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Contact Phone"
                        defaultValue="+1234567890"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="defaultCurrency"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Default Currency"
                        defaultValue="USD"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                  >
                    Save General Settings
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {tabValue === 1 && (
            <form onSubmit={handleSubmit(onEmailSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpHost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Host"
                        defaultValue="smtp.gmail.com"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpPort"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        label="SMTP Port"
                        defaultValue="587"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpUsername"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Username"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="password"
                        label="SMTP Password"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                    sx={{ mr: 2 }}
                  >
                    Save Email Settings
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Send />}
                  >
                    Test Email
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {tabValue === 2 && (
            <form onSubmit={handleSubmit(onWhatsAppSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="whatsappApiUrl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="WhatsApp API URL"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="whatsappApiKey"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="password"
                        label="WhatsApp API Key"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                    sx={{ mr: 2 }}
                  >
                    Save WhatsApp Settings
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Send />}
                  >
                    Test WhatsApp
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {tabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Booking Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure booking-related settings such as cancellation policies, refund rules, etc.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
